import { useCallback, useMemo, useState } from 'react'
import useSWR from 'swr'
import axios from 'axios'
import { useAuth0 } from '@auth0/auth0-react'

import useToken from './useToken'
import useEnrollmentTuple from './useEnrollmentTuple'

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL

function buildEnrollmentKey(record) {
  if (!record) {
    return ''
  }
  const accountId = (record.account_id || '').toString().trim()
  const address = (record.account_address || record.display_address || '')
    .toString()
    .trim()
    .toLowerCase()

  return `${accountId}::${address}`
}

function getSortTimestamp(record) {
  if (!record) {
    return 0
  }
  const lastUpdated = Date.parse(record.last_updated)
  const optIn = Date.parse(record.opt_in_date)

  if (!Number.isNaN(lastUpdated) && !Number.isNaN(optIn)) {
    return Math.max(lastUpdated, optIn)
  }
  if (!Number.isNaN(lastUpdated)) {
    return lastUpdated
  }
  if (!Number.isNaN(optIn)) {
    return optIn
  }
  return 0
}

function sortEnrollments(records = []) {
  return [...records].sort((a, b) => getSortTimestamp(b) - getSortTimestamp(a))
}

export default function useProfile() {
  const { user } = useAuth0()
  const {
    tuple,
    isLoading: tupleLoading,
    error: tupleError,
    refresh: refreshTuple,
  } = useEnrollmentTuple()
  const { headers, loading: tokenLoading, error: tokenError } = useToken()

  const [isLinking, setIsLinking] = useState(false)

  const shouldFetch =
    !!tuple && !!headers && !!user?.sub && typeof API_BASE_URL === 'string'

  const fetcher = useCallback(async () => {
    const { data } = await axios.post(
      `${API_BASE_URL}/enrollments/link/options`,
      {
        ...tuple,
        auth0_id: user.sub,
        email: user.email || '',
      },
      { headers }
    )
    return data
  }, [headers, tuple, user?.email, user?.sub])

  const swrKey = shouldFetch
    ? [
        'enrollment-link-options',
        tuple.account_id,
        tuple.street_address_1 || tuple.account_address,
        user.sub,
      ]
    : null

  const {
    data,
    error: optionsError,
    isValidating,
    mutate,
  } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
  })

  const claimEnrollment = useCallback(
    async (accountAddress) => {
      if (!shouldFetch || !accountAddress) {
        return null
      }
      setIsLinking(true)
      try {
        await axios.post(
          `${API_BASE_URL}/enrollments/link`,
          {
            ...tuple,
            auth0_id: user.sub,
            email: user.email || '',
            selected_account_address: accountAddress,
          },
          { headers }
        )
        await mutate()
      } finally {
        setIsLinking(false)
      }
      return true
    },
    [headers, mutate, shouldFetch, tuple, user?.email, user?.sub]
  )

  const linkedEnrollments = useMemo(() => {
    const raw = data?.linked
    const candidates = Array.isArray(raw) ? raw : raw ? [raw] : []

    const deduped = new Map()
    candidates.forEach((record) => {
      const key = buildEnrollmentKey(record)
      if (!deduped.has(key)) {
        deduped.set(key, record)
        return
      }
      const existing = deduped.get(key)
      if (getSortTimestamp(record) > getSortTimestamp(existing)) {
        deduped.set(key, record)
      }
    })

    return sortEnrollments(Array.from(deduped.values()))
  }, [data?.linked])

  const linkedKeys = useMemo(() => {
    return new Set(
      linkedEnrollments.map((record) => buildEnrollmentKey(record))
    )
  }, [linkedEnrollments])

  const primaryEnrollment = linkedEnrollments[0] || null

  const unclaimed = useMemo(() => {
    const raw = Array.isArray(data?.unclaimed) ? data.unclaimed : []
    if (raw.length === 0) {
      return []
    }
    const candidates = raw.filter(
      (record) => !linkedKeys.has(buildEnrollmentKey(record))
    )
    return sortEnrollments(candidates)
  }, [data?.unclaimed, linkedKeys])

  const claimedByOther = useMemo(() => {
    const raw = Array.isArray(data?.claimed_by_other)
      ? data.claimed_by_other
      : []
    return sortEnrollments(raw)
  }, [data?.claimed_by_other])

  const inactive = useMemo(() => {
    const raw = Array.isArray(data?.inactive) ? data.inactive : []
    return sortEnrollments(raw)
  }, [data?.inactive])

  const attemptInfo = data?.attempt || null

  const combinedError = useMemo(() => {
    return tupleError || tokenError || optionsError || null
  }, [tupleError, tokenError, optionsError])

  const isLoading =
    tupleLoading || tokenLoading || (shouldFetch && !data && !combinedError)

  return {
    profile: primaryEnrollment,
    linkedEnrollments,
    hasMultipleLinked: linkedEnrollments.length > 1,
    unclaimed,
    claimedByOther,
    inactive,
    attemptInfo,
    isLoading,
    isValidating,
    isLinking,
    error: combinedError,
    claimEnrollment,
    refresh: mutate,
    refreshTuple,
    tuple,
  }
}
